import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { FilterFieldConfig } from '../../lib/filterConfig';
import type { FilterOperator, FilterValue } from '../../types/analytics.types';
import { useFilterOptions } from '../../hooks/useAnalyticsQuery';

interface FilterValueInputProps {
  fieldConfig: FilterFieldConfig;
  operator: FilterOperator;
  value: FilterValue;
  onChange: (value: FilterValue) => void;
}

export function FilterValueInput({
  fieldConfig,
  operator,
  value,
  onChange,
}: FilterValueInputProps) {
  const needsDynamicOptions =
    fieldConfig.valueType === 'select' &&
    (!fieldConfig.options || fieldConfig.options.length === 0);

  const { data: dynamicOptions, isLoading: optionsLoading } = useFilterOptions(
    fieldConfig.field,
    needsDynamicOptions
  );

  const options = needsDynamicOptions
    ? dynamicOptions || []
    : fieldConfig.options || [];

  if (operator === 'is_true' || operator === 'is_false') {
    return null;
  }

  if (operator === 'is_null' || operator === 'is_not_null') {
    return null;
  }

  if (operator === 'between') {
    const rangeValue = (value as { from: number | string; to: number | string }) || {
      from: '',
      to: '',
    };

    return (
      <div className="flex items-center gap-2">
        <Input
          type={fieldConfig.valueType === 'number' ? 'number' : 'text'}
          placeholder="From"
          value={rangeValue.from ?? ''}
          onChange={(e) =>
            onChange({
              ...rangeValue,
              from: fieldConfig.valueType === 'number' ? Number(e.target.value) : e.target.value,
            })
          }
          className="w-24"
        />
        <span className="text-sm text-muted-foreground">and</span>
        <Input
          type={fieldConfig.valueType === 'number' ? 'number' : 'text'}
          placeholder="To"
          value={rangeValue.to ?? ''}
          onChange={(e) =>
            onChange({
              ...rangeValue,
              to: fieldConfig.valueType === 'number' ? Number(e.target.value) : e.target.value,
            })
          }
          className="w-24"
        />
        {fieldConfig.unit && (
          <span className="text-sm text-muted-foreground">{fieldConfig.unit}</span>
        )}
      </div>
    );
  }

  if (
    operator === 'within_last_days' ||
    operator === 'more_than_days_ago'
  ) {
    return (
      <div className="flex items-center gap-2">
        <Input
          type="number"
          placeholder="Days"
          value={value as number ?? ''}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-24"
          min={0}
        />
        <span className="text-sm text-muted-foreground">days</span>
        {operator === 'more_than_days_ago' && (
          <span className="text-sm text-muted-foreground">ago</span>
        )}
      </div>
    );
  }

  if (fieldConfig.valueType === 'select' || fieldConfig.valueType === 'multi_select') {
    if (operator === 'in' || operator === 'not_in') {
      const selectedValues: string[] = Array.isArray(value)
        ? (value as string[])
        : [];

      return (
        <div className="flex flex-wrap gap-1">
          <Select
            value=""
            onValueChange={(newValue: string) => {
              if (!selectedValues.includes(newValue)) {
                onChange([...selectedValues, newValue]);
              }
            }}
            disabled={optionsLoading}
          >
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue
                placeholder={optionsLoading ? 'Loading...' : 'Select values...'}
              />
            </SelectTrigger>
            <SelectContent>
              {options
                .filter((opt) => !selectedValues.includes(opt.value))
                .map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          {selectedValues.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {selectedValues.map((val: string) => {
                const option = options.find((o) => o.value === val);
                return (
                  <span
                    key={val}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-secondary"
                  >
                    {option?.label || val}
                    <button
                      type="button"
                      onClick={() => onChange(selectedValues.filter((v: string) => v !== val))}
                      className="hover:text-destructive"
                    >
                      ×
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    return (
      <Select
        value={value as string ?? ''}
        onValueChange={onChange}
        disabled={optionsLoading}
      >
        <SelectTrigger className="w-full sm:w-[180px]">
          <SelectValue
            placeholder={optionsLoading ? 'Loading...' : 'Select...'}
          />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (fieldConfig.valueType === 'number') {
    return (
      <div className="flex items-center gap-2">
        <Input
          type="number"
          placeholder={fieldConfig.placeholder || 'Enter value'}
          value={value as number ?? ''}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-32"
        />
        {fieldConfig.unit && (
          <span className="text-sm text-muted-foreground">{fieldConfig.unit}</span>
        )}
      </div>
    );
  }

  return (
    <Input
      type="text"
      placeholder={fieldConfig.placeholder || 'Enter value'}
      value={value as string ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-48"
    />
  );
}
